import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Tag,
  DatePicker,
  Tooltip,
  Avatar,
  Image,
  Select,
  Typography,
} from "antd";
import React, { useState, useContext, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import moment from "moment";
import * as XLSX from "xlsx";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import { useNavigate } from "react-router";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import UserFrom from "./UserFrom";
import ImportForm from "../../components/ImportForm";
import { AuthContext } from "../../context/AuthContext";
import { USERTYPE } from "../../constants/conts";
import { ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import confirm from "antd/lib/modal/confirm";
import FilterBar from "../../components/FilterBar";

const Search = Input.Search;
const { Title } = Typography;

function SessionManager() {
  const sectionName = "Session";
  const api = {
    status: apiPath.statusCustomer,
    delete: apiPath.deleteCustomer,
    addEdit: apiPath.addEditCustomer,
    // list: apiPath.listCustomer,
    importFile: apiPath.importCustomer,
  };

  const [searchText, setSearchText] = useState("");
  const { request } = useRequest();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ dau: 0, wau: 0, mau: 0 });
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [filters, setFilters] = useState({
    status: null,
    cricketRole: null,
  });
  const [showFilters, setShowFilters] = useState(false);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const debouncedSearchText = useDebounce(searchText, 300);

  const columns = [
    {
      title: "Name",
      dataIndex: ["userDetails", "name"],
      key: "name",
      width: 200,
      render: (_, { userDetails }) => {
        const profileImage = userDetails?.avatar || userDetails?.profilePic;
        const capitalizedName =
          userDetails && userDetails?.name
            ? userDetails.name.charAt(0).toUpperCase() +
            userDetails.name.slice(1)
            : "Not Available";

        return (
          <>
            {profileImage ? (
              <Image className="image-index-radius" src={profileImage} />
            ) : (
              <Avatar size={40}>
                {userDetails?.name ? userDetails.name.charAt(0) : "N/A"}
              </Avatar>
            )}
            <a className="avatar-text">{capitalizedName}</a>
          </>
        );
      },
    },
    {
      title: "Username",
      dataIndex: ["userDetails", "username"],
      key: "username",
      width: 200,
      render: (_, { userDetails }) => {
        return (
          <>
            {userDetails && userDetails.username ? (
              <a className="avatar-text">{userDetails.username || "-"}</a>
            ) : (
              "N/A"
            )}
          </>
        );
      },
    },
    {
      title: "Email Address",
      dataIndex: ["userDetails", "email"],
      width: 220,
      render: (_, { userDetails }) => {
        return (
          <>
            {userDetails && userDetails.email ? (
              <p>{userDetails.email}</p>
            ) : (
              "N/A"
            )}
          </>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 120,
      render: (_, { role }) => role || "-",
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      width: 180,
      render: (_, { ipAddress }) => ipAddress || "-",
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivityAt",
      width: 200,
      render: (_, { lastActivityAt }) =>
        lastActivityAt
          ? moment(lastActivityAt).format("YYYY-MM-DD | HH:mm A")
          : "-",
    },
    {
      title: "Status",
      render: (_, { is_delete, isActive, _id }) =>
        !is_delete ? (
          <Tag color={isActive === true ? "green" : "red"}>
            {isActive === true ? "Active" : "Inactive"}
          </Tag>
        ) : (
          <Tag color="red">Account Deleted</Tag>
        ),
    },
    {
      title: "Action",

      render: (_, record) => (
        <>
          <Tooltip title="Terminate session">
            <Button onClick={() => handleTerminate(record._id)}>
              <i className="fa fa-solid fa-ban"></i>
            </Button>
          </Tooltip>
        </>
      ),
    },
  ];

  const fetchData = (pagination, filters) => {
    const params = new URLSearchParams();
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }
    // 📄 Pagination
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);
    request({
      url: `${apiPath.listSession}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.data);
        setPagination({
          current: pagination.current,
          total: data.data.total,
          pageSize: Number(data.data.limit),
        });
        if (data.data.stats) {
          setStats(data.data.stats);
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleTerminate = (id) => {
    setTimeout(() => {
      confirm({
        maskClosable: true,
        icon: <ExclamationCircleOutlined />,
        className: "tab_modal",
        cancelButtonProps: { color: "red" },
        content: (
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Are you sure you want to terminate this session ?
          </p>
        ),
        onOk() {
          request({
            url: `${apiPath.terminateSession}/${id}`,
            method: "PUT",
            data: {},
            onSuccess: (data) => {
              fetchData(pagination, filters);
              ShowToast(
                `${data.message || "Terminated Successfully"}`,
                Severty.SUCCESS,
              );
            },
            onError: (error) => {
              const message = error.response.data.message;
              setLoading(false);
              ShowToast(message, Severty.ERROR);
            },
          });
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };

  // ------------ Module Permissions ------------

  const { userProfile, userPermission } = useContext(AuthContext);
  const isSuperAdmin = userProfile?.role === USERTYPE.SUPER_ADMIN;

  const isAddPermissionGranted =
    isSuperAdmin ||
    (userPermission?.USER_MANAGER?.enabled === true &&
      userPermission.USER_MANAGER.permissions.create === true);
  const isUpdatePermissionGranted =
    isSuperAdmin ||
    (userPermission?.USER_MANAGER?.enabled === true &&
      userPermission.USER_MANAGER.permissions.update === true);

  useEffect(() => {
    setLoading(true);
    fetchData(pagination, filters);
  }, [filters, refresh, debouncedSearchText, startDate, endDate]);

  const activeUserWidgets = [
    {
      label: "Daily Active Users (DAU)",
      sublabel: "Active in last 24 hours",
      value: stats?.dau ?? 0,
      icon: <i className="fas fa-user-clock"></i>,
    },
    {
      label: "Weekly Active Users (WAU)",
      sublabel: "Active in last 7 days",
      value: stats?.wau ?? 0,
      icon: <i className="fas fa-user-check"></i>,
    },
    {
      label: "Monthly Active Users (MAU)",
      sublabel: "Active in last 30 days",
      value: stats?.mau ?? 0,
      icon: <i className="fas fa-users"></i>,
    },
  ];

  return (
    <>
      <div className="tabled customerMain">
        {/* Active User Widgets (DAU, WAU, MAU) */}
        <Row gutter={[16, 24]} className="mb-24">
          {activeUserWidgets.map((widget, idx) => (
            <Col xs={24} sm={12} md={8} key={idx}>
              <Card bordered={false} className="criclebox" style={{ height: "100%" }}>
                <div className="number">
                  <Row align="middle" gutter={[24, 24]}>
                    <Col xs={18}>
                      <span>{widget.label}</span>
                      <Title level={3} style={{ margin: "4px 0" }}>{widget.value}</Title>
                      <span style={{ fontSize: "12px", color: "#8c8c8c" }}>{widget.sublabel}</span>
                    </Col>
                    <Col xs={6}>
                      <div className="icon-box" style={{ width: "45px", height: "45px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {widget.icon}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Session Manager"
              extra={
                <Search
                  allowClear
                  placeholder="Search"
                  onChange={(e) => setSearchText(e.target.value)}
                />
              }
            >
              <h4 className="text-right">{ShowTotal(pagination.total || 0)}</h4>
              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={pagination}
                onChange={fetchData}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <UserFrom
          sectionName={sectionName}
          path={api.addEdit}
          show={visible}
          hide={() => setVisible(false)}
          data={selected}
          refresh={() => setRefresh((p) => !p)}
        />
      )}

      {importVisible && (
        <ImportForm
          path={api.importFile}
          sectionName={sectionName}
          show={importVisible}
          hide={() => setImportVisible(false)}
          refresh={() => setRefresh((p) => !p)}
        />
      )}
    </>
  );
}

export default SessionManager;
